import { Component, inject, OnDestroy, output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, Subject, takeUntil, tap } from 'rxjs';

interface ValidationRule {
  type: string;
  message: string;
}

interface FieldValidationRules {
  referralCode: ValidationRule[];
}

@Component({
  selector: 'app-referral-code',
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './referral-code.component.html',
  styleUrl: './referral-code.component.scss',
})
export class ReferralCodeComponent implements OnDestroy {
  referralCodeForm!: FormGroup;
  isSubmitting = false;
  shouldShowValidationErrors = false;
  errorMessage = '';

  // Destroy subject for memory leak prevention
  private readonly destroy$ = new Subject<void>();

  // Centralized validation rules object
  fieldValidationRules: FieldValidationRules = {
    referralCode: [
      {
        type: 'invalidReferralCode',
        message: 'authentication.referral_code_invalid_error',
      },
    ],
  };

  private readonly formBuilder = inject(FormBuilder);

  public skipReferralHandling = output();

  private readonly authService = inject(AuthService);

  constructor() {
    this.initializeReferralCodeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes the referral code form with validation rules.
   */
  private initializeReferralCodeForm(): void {
    this.referralCodeForm = this.formBuilder.group({
      referralCode: ['', [Validators.pattern(/^[a-zA-Z0-9]+$/)]],
    });

    // Clear error messages when user modifies form
    this.referralCodeForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.errorMessage = '';
      this.shouldShowValidationErrors = false;

      // Clear specific field errors
      const referralCodeControl = this.referralCodeForm.get('referralCode');
      if (referralCodeControl?.hasError('invalidReferralCode')) {
        referralCodeControl.setErrors(null);
      }
    });
  }

  /**
   * Handles referral code form submission.
   */
  handleReferralCodeSubmission(): Observable<unknown> {
    return this.authService
      .handleReferralCode(this.referralCodeForm.getRawValue().referralCode)
      .pipe(
        takeUntil(this.destroy$),
        tap((response: unknown) => {
          // Check if response indicates success or failure
          const result = response as { success?: boolean; error?: string };
          if (result?.success === false) {
            this.shouldShowValidationErrors = true;
            const referralCodeControl = this.referralCodeForm.get('referralCode');
            referralCodeControl?.setErrors({ invalidReferralCode: true });
          } else {
            // Success - close modal
            this.skipReferralHandling.emit();
          }
        })
      );
  }

  /**
   * Helper method to get field error message from centralized validation rules.
   */
  getFieldErrorMessage(fieldName: keyof FieldValidationRules): string {
    const formControl = this.referralCodeForm.get(fieldName);
    if (!formControl?.errors) return '';

    const fieldRules = this.fieldValidationRules[fieldName];
    if (!fieldRules) return '';

    for (const rule of fieldRules) {
      if (formControl.hasError(rule.type)) {
        return rule.message;
      }
    }
    return '';
  }
}
